import { useCurrentUser } from '@artic/api-client';
import i18next, { type i18n as I18nInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { useEffect, type ReactNode } from 'react';
import { I18nextProvider, initReactI18next, useTranslation } from 'react-i18next';

import { mergePartnerOverrides } from './partner-overlay';
import type { PartnerResourceLoader, ResourceLoader } from './types';

export interface CreateI18nOptions {
  namespaces: readonly string[];
  defaultNS: string;
  fallbackLng?: string;
  fallbackNS?: string;
  supportedLngs?: readonly string[];
  resources: ResourceLoader;
  partnerResources: PartnerResourceLoader;
  /** Cookie name read by LanguageDetector. Defaults to `artic_lang`. */
  languageCookie?: string;
}

export interface CreateI18nResult {
  i18n: I18nInstance;
  I18nProvider: (props: { children: ReactNode }) => ReactNode;
  I18nPartnerSync: () => null;
}

export function createI18n(opts: CreateI18nOptions): CreateI18nResult {
  const instance = i18next.createInstance();

  const initOptions = {
    fallbackLng: opts.fallbackLng ?? 'en',
    defaultNS: opts.defaultNS,
    ns: [...opts.namespaces],
    fallbackNS: opts.fallbackNS ?? opts.defaultNS,
    interpolation: { escapeValue: false },
    react: { useSuspense: true },
    ...(opts.supportedLngs ? { supportedLngs: [...opts.supportedLngs] } : {}),
    detection: {
      order: ['cookie', 'localStorage', 'navigator'],
      caches: ['cookie', 'localStorage'],
      lookupCookie: opts.languageCookie ?? 'artic_lang',
    },
  };

  // Kick off initialisation. With `useSuspense: true`, translated components
  // suspend on the nearest boundary until their namespace has loaded, so we
  // don't need to await the promise here — but we must not silently discard it,
  // or an init failure would surface only as an unhandled rejection.
  instance
    .use(LanguageDetector)
    .use(resourcesToBackend(opts.resources))
    .use(initReactI18next)
    .init(initOptions)
    .catch((err: unknown) => {
      console.error('[@artic/i18n] init failed', err);
    });

  function I18nProvider({ children }: { children: ReactNode }): ReactNode {
    return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
  }

  function I18nPartnerSync(): null {
    const { activePartner } = useCurrentUser();
    const { i18n } = useTranslation();

    useEffect(() => {
      mergePartnerOverrides(
        i18n,
        i18n.language,
        activePartner,
        opts.partnerResources,
        opts.resources,
      ).catch((err: unknown) => {
        console.error('[@artic/i18n] partner overlay merge failed', err);
      });
    }, [activePartner, i18n, i18n.language, opts.partnerResources, opts.resources]);

    return null;
  }

  return { i18n: instance, I18nProvider, I18nPartnerSync };
}
